import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { get } from '../../helpers/httpClient';
import { toast } from 'react-toastify';
import General from './internal/General';
import Structure from './internal/Structure';
import Menu from './internal/Menu';
import Visual from './internal/Visual';
import Social from './internal/Social';
import Layouts from './internal/Layouts';
import Transport from './internal/Transport';
import Widgets from './internal/Widgets';

function WebsiteProfile() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [burgerOpen, setBurgerOpen] = useState(false);

  const tabFromQuery = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromQuery || 'structure');

  useEffect(() => {
    const nextTab = tabFromQuery || 'structure';
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromQuery]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError('');
    get(`websites/${id}`, (resp) => {
      if (!isMounted) return;
      // accept either resp.website or resp itself
      setWebsite(resp?.website || resp || null);
    })
      .catch(() => isMounted && toast.error('Failed to load website.'))
      .finally(() => isMounted && setLoading(false));
    return () => { isMounted = false; };
  }, [id, activeTab]);

  const handleSelect = (key) => {
    setBurgerOpen(false);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (key === 'structure') {
        p.delete('tab');
      } else {
        p.set('tab', key);
      }
      return p;
    });
  };

  const getModule = (tab) => {
    switch (tab) {
      case 'general':
        return General;
      case 'menu':
        return Menu;
      case 'visual':
        return Visual;
      case 'social':
        return Social;
      case 'layouts':
        return Layouts;
      case 'transport':
        return Transport;
      case 'widgets':
        return Widgets;
      case 'structure':
      default:
        return Structure;
    }
  };

  const CurrentModule = getModule(activeTab);

  const TAB_LABELS = {
    general: 'General',
    structure: 'Structure',
    layouts: 'Layouts',
    menu: 'Menu',
    visual: 'Visual',
    social: 'Social',
    transport: 'Transport',
    widgets: 'Widgets',
  };
  const activeTabLabel = TAB_LABELS[activeTab] || (activeTab.charAt(0).toUpperCase() + activeTab.slice(1));

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <h1>{website?.title || 'Website Profile'}</h1>
        </Col>
      </Row>

      {website && website.domainBound === false && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger">
              <Alert.Heading>Domain not pointed at this server</Alert.Heading>
              <p>
                <strong>{website.domain}</strong> does not currently resolve to this server
                {website.serverIp && <> (it should point to <code>{website.serverIp}</code>)</>}.
                The website won't be reachable at this domain, and an SSL certificate can't be
                issued for it, until DNS is fixed.
              </p>
              <hr />
              <p className="mb-0">
                To fix this: open your domain's DNS settings at your registrar and add (or
                update) an <strong>A record</strong> for <code>{website.domain}</code>
                {website.serverIp && <> pointing to <code>{website.serverIp}</code></>}. DNS
                changes can take anywhere from a few minutes up to several hours to propagate.
              </p>
            </Alert>
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="text-center my-4"><Spinner animation="border" /></div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : !website ? (
        <Alert variant="warning">Website not found.</Alert>
      ) : (
        <Row>
          <Col>
            <Card>
              <Card.Body>
                <div className="profile-tabs mb-3">
                  <div className="tabs-row" role="tablist" aria-label="Profile sections">
                    <button type="button" className={`login-button tab-btn ${activeTab === 'general' ? 'active' : ''}`} onClick={() => handleSelect('general')} role="tab" aria-selected={activeTab === 'general'}>General</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'structure' ? 'active' : ''}`} onClick={() => handleSelect('structure')} role="tab" aria-selected={activeTab === 'structure'}>Structure</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'layouts' ? 'active' : ''}`} onClick={() => handleSelect('layouts')} role="tab" aria-selected={activeTab === 'layouts'}>Layouts</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'menu' ? 'active' : ''}`} onClick={() => handleSelect('menu')} role="tab" aria-selected={activeTab === 'menu'}>Menu</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'visual' ? 'active' : ''}`} onClick={() => handleSelect('visual')} role="tab" aria-selected={activeTab === 'visual'}>Visual</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'social' ? 'active' : ''}`} onClick={() => handleSelect('social')} role="tab" aria-selected={activeTab === 'social'}>Social</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'transport' ? 'active' : ''}`} onClick={() => handleSelect('transport')} role="tab" aria-selected={activeTab === 'transport'}>Transport</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'widgets' ? 'active' : ''}`} onClick={() => handleSelect('widgets')} role="tab" aria-selected={activeTab === 'widgets'}>Widgets</button>
                  </div>

                  <div className={`tabs-burger ${burgerOpen ? 'open' : ''}`}>
                    <button type="button" className="login-button" aria-haspopup="listbox" aria-expanded={burgerOpen} id="tabs-burger-btn" onClick={() => setBurgerOpen(o => !o)}>
                      {activeTabLabel} ▾
                    </button>
                    <ul className="burger-menu" role="listbox" aria-labelledby="tabs-burger-btn">
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'general' ? 'active' : ''}`} onClick={() => handleSelect('general')}>General</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'structure' ? 'active' : ''}`} onClick={() => handleSelect('structure')}>Structure</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'layouts' ? 'active' : ''}`} onClick={() => handleSelect('layouts')}>Layouts</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'menu' ? 'active' : ''}`} onClick={() => handleSelect('menu')}>Menu</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'visual' ? 'active' : ''}`} onClick={() => handleSelect('visual')}>Visual</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'social' ? 'active' : ''}`} onClick={() => handleSelect('social')}>Social</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'transport' ? 'active' : ''}`} onClick={() => handleSelect('transport')}>Transport</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'widgets' ? 'active' : ''}`} onClick={() => handleSelect('widgets')}>Widgets</button></li>
                    </ul>
                  </div>
                </div>

                <CurrentModule website={website} onUpdated={(w) => setWebsite(w)} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default WebsiteProfile;
